import { useMemo, useState } from 'react'
import { Capacitor } from '@capacitor/core'
import { Browser } from '@capacitor/browser'
import { BackgroundTitle } from '../../../components/BackgroundTitle'
import { PageHeader } from '../../../components/PageHeader'
import { PPG_GAMES, type PpgMeta } from '../data/games'
import { useT } from '../../../i18n'

/**
 * The printed book's product page. Opens OUTSIDE the app on purpose:
 * the book is a physical good, so App Review guideline 3.1.5(a)
 * requires it be bought by some means other than in-app purchase.
 * Keep the wording unmistakably about the physical book so nobody
 * reads it as unlocking app content or as part of All-Access.
 */
const BOOK_URL = 'https://jordandraper.com/games/portable-party-games'

/**
 * Tagging lives in the URL rather than relying on the referrer, because
 * the native path throws the referrer away: Browser.open() hands off to
 * an SFSafariViewController with a fresh context, so those visits land
 * in the storefront's Direct bucket, indistinguishable from someone
 * typing the address. The web path does send a referrer, but a
 * cross-origin one is trimmed to the bare origin by default, so it can
 * say "the app" and nothing more.
 *
 * Source splits ios/web so the two are separable in analytics; that
 * split is the whole point, since the iOS half was previously invisible.
 */
function bookUrl(): string {
  const source = Capacitor.isNativePlatform() ? 'jdg-app-ios' : 'jdg-app-web'
  return `${BOOK_URL}?utm_source=${source}&utm_medium=app&utm_campaign=portable-party`
}

function openBook(): void {
  const url = bookUrl()
  if (Capacitor.isNativePlatform()) void Browser.open({ url })
  else window.open(url, '_blank', 'noopener')
}

interface Props {
  onBack: () => void
  onOpenGame: (gameNumber: number) => void
}

const PPG_RED = '#EF4B03'
const JDG_YELLOW = '#B8C01F'   // brand lime-dark — legible at row size

type FilterKey = keyof PpgMeta

// Labels are translated inside the component (FILTER_LABEL), so this
// module-scope list carries only the keys.
const FILTERS: FilterKey[] = ['players', 'time', 'ages', 'origin']

/**
 * Portable Party Games — the 50-slot index. Filled slots (title set)
 * are tappable; the rest render as numbered COMING SOON rows so the
 * shape of the full book is visible.
 *
 * The filter row is data-driven: each dropdown's options are the
 * distinct values across filled games, so it grows automatically as
 * games are imported. While any filter is set, COMING SOON slots are
 * hidden (they have no meta to match).
 */
export function PortablePartyListScreen({ onBack, onOpenGame }: Props) {
  const t = useT()
  const [filters, setFilters] = useState<Record<FilterKey, string>>({
    players: '', time: '', ages: '', origin: '',
  })
  const filterActive = Object.values(filters).some(Boolean)

  // FILTERS lives at module scope (no hook allowed there), so the
  // dropdown labels are translated here, with literal keys.
  const FILTER_LABEL: Record<FilterKey, string> = {
    players: t('Players'),
    time:    t('Time'),
    ages:    t('Ages'),
    origin:  t('Origin'),
  }

  const options = useMemo(() => {
    const filled = PPG_GAMES.filter(g => g.title !== null && g.meta)
    // Sort by the leading number when there is one ("5 min" before
    // "10 min", "2+" before "10+"); plain alphabetical otherwise
    // (origins). A bare .sort() put "10 min" before "5 min".
    const numeric = (v: string) => {
      const m = v.match(/^\d+/)
      return m ? parseInt(m[0], 10) : Number.POSITIVE_INFINITY
    }
    const distinct = (key: FilterKey) =>
      [...new Set(filled.map(g => g.meta![key]))]
        .sort((a, b) => (numeric(a) - numeric(b)) || a.localeCompare(b))
    return {
      players: distinct('players'),
      time:    distinct('time'),
      ages:    distinct('ages'),
      origin:  distinct('origin'),
    }
  }, [])

  const rows = filterActive
    ? PPG_GAMES.filter(g =>
        g.title !== null && g.meta &&
        FILTERS.every(key => !filters[key] || g.meta![key] === filters[key]))
    : PPG_GAMES

  return (
    <div className="screen">
      <BackgroundTitle text={t('PORTABLE PARTY')} color={PPG_RED} />
      <PageHeader
        title={t('Portable Party Games')}
        subtitle={t('50 games from around the world · no components')}
        onBack={onBack}
      />

      <div className="flex-1 overflow-y-auto px-5 pb-10 relative z-10">
        {/* Physical book, sold on the website. Deliberately small and
            deliberately NOT styled like a paywall or an unlock. */}
        <button
          type="button"
          onClick={openBook}
          className="w-full mb-3 rounded-2xl border-2 border-game-portable-party-dark
                     bg-game-portable-party-light px-3 py-2 flex items-center gap-3
                     text-left active:scale-[0.99] transition-transform"
        >
          <img
            src="/games/portable-party.webp"
            alt=""
            className="w-9 h-9 rounded-lg object-cover shrink-0 shadow-xs"
          />
          <span className="flex-1 min-w-0">
            <span className="block text-[13px] font-semibold text-brand-text leading-tight">{t('Pre-order the printed book')}</span>
            <span className="block text-[11px] text-brand-muted leading-tight">{t('All 50 games in print · ships from jordandraper.com')}</span>
          </span>
          {/* External-link glyph, so the tap's destination is obvious. */}
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" className="shrink-0"
               aria-hidden="true">
            <path d="M14 4h6v6M20 4l-8 8M18 14v5a1 1 0 01-1 1H5a1 1 0 01-1-1V7a1 1 0 011-1h5"
                  stroke={PPG_RED} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>

        {/* Filter row — 2×2 grid of compact selects + Clear when active. */}
        <div className="grid grid-cols-2 gap-2 mb-3">
          {FILTERS.map(key => (
            <select
              key={key}
              value={filters[key]}
              onChange={e => setFilters(f => ({ ...f, [key]: e.target.value }))}
              className={`w-full text-xs font-semibold rounded-full px-3 py-2
                          border appearance-none
                          focus:outline-hidden focus:bg-brand-lime focus:border-brand-lime-dark
                          focus:text-brand-text
                          ${filters[key]
                            ? 'bg-game-portable-party-light border-game-portable-party-dark text-game-portable-party-dark'
                            : 'bg-white border-brand-border text-brand-text'}`}
            >
              <option value="">{t('{label} · Any', { label: FILTER_LABEL[key] })}</option>
              {options[key].map(v => (
                <option key={v} value={v}>{v}</option>
              ))}
            </select>
          ))}
        </div>
        {filterActive && (
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs text-brand-muted">
              {rows.length === 1
                ? t('1 game · coming-soon slots hidden')
                : t('{n} games · coming-soon slots hidden', { n: rows.length })}
            </p>
            <button
              type="button"
              onClick={() => setFilters({ players: '', time: '', ages: '', origin: '' })}
              className="text-xs font-semibold text-game-portable-party-dark
                         bg-game-portable-party-light border border-game-portable-party-dark/40
                         rounded-full px-3 py-1 active:scale-95 transition-transform"
            >{t('Clear filters')}</button>
          </div>
        )}

        <div className="card p-4">
          {rows.length === 0 && (
            <p className="text-sm text-brand-muted py-2">{t('No games match those filters yet. More games are on the way.')}</p>
          )}
          {rows.map(game => {
            const filled = game.title !== null
            const accentClass = game.jdg ? 'text-brand-lime-dark' : 'text-game-portable-party-dark'
            const chevron = game.jdg ? JDG_YELLOW : PPG_RED
            return (
              <button
                key={game.number}
                type="button"
                disabled={!filled}
                onClick={filled ? () => onOpenGame(game.number) : undefined}
                className={`w-full flex items-center gap-3 py-2.5 text-left
                            border-b border-brand-border/40 last:border-b-0
                            ${filled ? 'active:scale-[0.99] cursor-pointer' : 'cursor-default'}`}
              >
                <span
                  className={`w-9 shrink-0 font-display font-black text-sm tracking-wide
                              ${filled ? accentClass : 'text-brand-muted/60'}`}
                >
                  {String(game.number).padStart(2, '0')}
                </span>
                {filled ? (
                  <>
                    <span className={`flex-1 font-display font-black uppercase tracking-widest ${accentClass}`}>
                      {game.title}
                    </span>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                         className="shrink-0" aria-hidden="true">
                      <path d="M9 6l6 6-6 6" stroke={chevron} strokeWidth="2"
                            strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </>
                ) : (
                  <>
                    <span className="flex-1 font-display font-bold uppercase tracking-widest
                                     text-brand-muted/60">
                      {t('Game {n}', { n: game.number })}
                    </span>
                    <span className="shrink-0 text-[10px] font-semibold text-brand-muted
                                     bg-brand-elevated px-2 py-0.5 rounded-full
                                     border border-brand-border tracking-wide">
                      COMING SOON
                    </span>
                  </>
                )}
              </button>
            )
          })}
        </div>

        <p className="text-xs text-brand-muted mt-4">{t('Every game is rules-only: nothing to carry, nothing to set up. More games unlock as they are added.')}</p>
      </div>
    </div>
  )
}
